import { Badge, Box, Group, Text } from '@mantine/core';
import type { KeyboardEvent } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { groupCountLabel, groupTitle, groupWhere, memberLabel, type EventGroupItem } from './groupEvents';
import { eventEmoji, formatSpan, severityDotColor, type TimelineEventView } from './timelineEvent';
import { TIMELINE_TITLE_FZ, timelineCardFrame, timelineTitleStyle } from './TimelineEventCard';
import { TimelineEventRow } from './TimelineEventRow';

export interface TimelineEventGroupProps<V extends TimelineEventView = TimelineEventView> {
  /** A group from `groupEvents()`. */
  group: EventGroupItem<V>;
  expanded: boolean;
  onToggle: () => void;
  onEventClick?: (event: V) => void;
}

/** A burst of related events on the Timeline: a header card (headline title,
 *  "N events", time span, actors, place) and, when expanded, compact member
 *  rows indented under it. One alarm zone across floors reads
 *  "Alarm: Disarmed — <zone>" with "5 floors", its members one row per floor. */
export function TimelineEventGroup<V extends TimelineEventView = TimelineEventView>({
  group,
  expanded,
  onToggle,
  onEventClick,
}: TimelineEventGroupProps<V>) {
  const { headline, events } = group;
  const Chevron = expanded ? ChevronDown : ChevronRight;
  const where = groupWhere(group);
  const title = groupTitle(group);
  return (
    <Box data-event-group={group.key}>
      <Box
        p={16}
        style={{ ...timelineCardFrame, backgroundColor: 'var(--mantine-color-default-hover)' }}
        onClick={onToggle}
        onKeyDown={(e: KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
          }
        }}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        aria-label={`${expanded ? 'Collapse' : 'Expand'} ${events.length} related events`}
      >
        <Group align="flex-start" gap={16} wrap="nowrap">
          <Box w={12} h={12} mt={8} style={{ borderRadius: 9999, flexShrink: 0, backgroundColor: severityDotColor(headline.severity) }} />
          <Box miw={0} flex={1}>
            <Group mb="xs" gap={8} wrap="nowrap" align="baseline">
              <Text component="span" fz="sm">{eventEmoji(headline.kind, headline.title)}</Text>
              <Text
                component="h3"
                data-timeline-title
                title={title}
                fz={`${TIMELINE_TITLE_FZ}px`}
                fw={500}
                lh={1.55}
                lineClamp={2}
                style={{ ...timelineTitleStyle, overflowWrap: 'anywhere' }}
              >
                {title}
              </Text>
              <Badge variant="light" color="gray" size="sm" tt="none" style={{ flexShrink: 0 }}>
                {groupCountLabel(group)}
              </Badge>
            </Group>
            <Group fz="xs" c="dimmed" gap={16} wrap="wrap">
              <span>{formatSpan(group.start, group.end)}</span>
              {group.actors.length > 0 && <span>👤 {group.actors.join(', ')}</span>}
              {where && <span>📍 {where}</span>}
            </Group>
          </Box>
          <Chevron size={18} color="var(--mantine-color-dimmed)" style={{ flexShrink: 0, marginTop: 4 }} />
        </Group>
      </Box>
      {expanded && (
        <Box
          mt={6}
          ml={{ base: 12, sm: 22 }}
          style={{ borderLeft: '2px solid var(--mantine-color-default-border)' }}
        >
          {events.map((event) => (
            <TimelineEventRow
              key={event.id}
              event={event}
              groupStart={group.start}
              onClick={onEventClick}
              label={memberLabel(group, event)}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}
